/**
 * @fileoverview Utility for chunking large text content with overlap.
 *
 * Used for processing epic-length documents that exceed LLM context limits.
 * Splits text into manageable chunks while preserving context through overlap.
 */

import { ChunkInfo, ChunkResult } from './schemas';

/**
 * Default chunk size in characters (~100k tokens assuming 4 chars/token).
 */
export const DEFAULT_CHUNK_SIZE = 400_000;

/**
 * Default overlap size in characters (~10k tokens).
 */
export const DEFAULT_OVERLAP_SIZE = 40_000;

/**
 * Splits text into chunks with configurable overlap.
 *
 * Each chunk includes overlap from adjacent chunks to ensure context continuity:
 * - First chunk: text[0..chunkSize+overlap]
 * - Middle chunks: text[start-overlap..start+chunkSize+overlap]
 * - Last chunk: text[start-overlap..end]
 *
 * @param text - The full text to split
 * @param chunkSize - Maximum size of each chunk's core content (default: 400k chars)
 * @param overlapSize - Size of overlap on each side (default: 40k chars)
 * @returns Array of chunk results with text and metadata
 *
 * @example
 * const chunks = chunkText(longDocument, 400000, 40000);
 * // Process sequentially:
 * for (const chunk of chunks) {
 *   const result = await processChunk(chunk.text, chunk.chunkInfo);
 * }
 */
export function chunkText(
  text: string,
  chunkSize: number = DEFAULT_CHUNK_SIZE,
  overlapSize: number = DEFAULT_OVERLAP_SIZE
): ChunkResult[] {
  // Validation
  if (!text || text.length === 0) {
    return [];
  }

  if (chunkSize <= 0) {
    throw new Error('chunkSize must be a positive number');
  }

  if (overlapSize < 0) {
    throw new Error('overlapSize cannot be negative');
  }

  if (overlapSize >= chunkSize) {
    throw new Error('overlapSize must be less than chunkSize');
  }

  // If text fits in a single chunk, return it as-is
  if (text.length <= chunkSize) {
    return [{
      text,
      chunkInfo: {
        chunkIndex: 0,
        totalChunks: 1,
        isFirstChunk: true,
        isLastChunk: true,
        coreStartOffset: 0,
        coreEndOffset: text.length,
      },
    }];
  }

  const chunks: ChunkResult[] = [];

  // Calculate how many chunks we need
  // Each chunk advances by (chunkSize - no double-counting the core)
  // The stride is the core chunk size (without overlap)
  const stride = chunkSize;
  const totalChunks = Math.ceil(text.length / stride);

  for (let i = 0; i < totalChunks; i++) {
    const isFirstChunk = i === 0;
    const isLastChunk = i === totalChunks - 1;

    // Core content boundaries (the "main" part of this chunk)
    const coreStart = i * stride;
    const coreEnd = Math.min(coreStart + stride, text.length);

    // Actual extraction boundaries (including overlap)
    // Leading overlap: look back from core start (except for first chunk)
    const extractStart = isFirstChunk ? 0 : Math.max(0, coreStart - overlapSize);
    // Trailing overlap: look ahead from core end (except for last chunk)
    const extractEnd = isLastChunk ? text.length : Math.min(text.length, coreEnd + overlapSize);

    const chunkText = text.slice(extractStart, extractEnd);

    const chunkInfo: ChunkInfo = {
      chunkIndex: i,
      totalChunks,
      isFirstChunk,
      isLastChunk,
      coreStartOffset: coreStart,
      coreEndOffset: coreEnd,
    };

    chunks.push({
      text: chunkText,
      chunkInfo,
    });
  }

  return chunks;
}

/**
 * Estimates how many chunks a text would produce without actually chunking it.
 * Useful for progress indication before processing begins.
 *
 * @param textLength - Length of the text in characters
 * @param chunkSize - Maximum size of each chunk's core content
 * @returns Estimated number of chunks
 */
export function estimateChunkCount(
  textLength: number,
  chunkSize: number = DEFAULT_CHUNK_SIZE
): number {
  if (textLength <= 0 || chunkSize <= 0) {
    return 0;
  }
  if (textLength <= chunkSize) {
    return 1;
  }
  return Math.ceil(textLength / chunkSize);
}

/**
 * Checks if a text requires chunking based on size.
 *
 * @param textLength - Length of the text in characters
 * @param chunkSize - Maximum size threshold
 * @returns True if text exceeds chunk size and needs to be split
 */
export function requiresChunking(
  textLength: number,
  chunkSize: number = DEFAULT_CHUNK_SIZE
): boolean {
  return textLength > chunkSize;
}
